import {
  Heading,
  TableView,
  View,
  TableHeader,
  
  Column,
  Cell,
  Row,
  TableBody,
  Flex,
  Text,
  TextField,
  Picker,
  Link,
  Item,
  TooltipTrigger,
  Tooltip,
  DateRangePicker,
  SearchField,
} from "@adobe/react-spectrum";
import React, { useEffect, useState } from "react";
import styles from "./CallbackDashboard.module.css";
import { getScheduledCallbackListApi } from "../../../src/api/session-api";
import {
  CallbackStatus,
  ColumnConfig,
} from "./types";
import {
  CallbackListAPIResponse,
  CallbackObjectType,
} from "../../types/callback.type";
import {
  callbackStatus,
  callbackTableColumns, STATUS_COLORS,
  callbackTypes,
  callbackSurface,
} from "./constants";
import {
  getAmazonConnectUrlByContactId,
  getSafeUrl,
} from "../../../src/utils/utils";
import Timer from "./Timer";
import { parseDate, today, CalendarDate, getLocalTimeZone } from "@internationalized/date";
import { ColumnStaticSize } from "@react-types/table";
import { SearchIcon } from "../common/Icons";

// Interface for ContactLink props for better type safety
interface ContactLinkProps {
  value: string;
  isConnectContact?: boolean;
  index?: number;
}

// Moved outside of parent component
const ContactLink = React.memo(({
  value,
  isConnectContact = false,
  index
}: ContactLinkProps) => {
  // Calculate memoized values first, before any conditional returns
  const contactUrl = React.useMemo(() => 
    value && typeof value === 'string' ? getAmazonConnectUrlByContactId(value) : null
  , [value]);
  
  const safeUrl = React.useMemo(() => 
    contactUrl ? getSafeUrl(contactUrl) : null
  , [contactUrl]);
  
  const displayText = React.useMemo(() => {
    if (!value || typeof value !== 'string') return "-";
    
    if (isConnectContact) {
      return `Attempt ${index !== undefined ? index + 1 : ""}`;
    } else {
      // Prevent substring errors by checking length
      // return value.length > 8 ? `${value.substring(0, 8)}...` : value;
      return value;
    }
  }, [isConnectContact, index, value]);
  
  // Handle empty or invalid values after hooks are called
  if (!value || typeof value !== 'string') return <Text>-</Text>;
  
  // Use either Link or Text based on URL availability
  return safeUrl ? (
    <Link
      href={safeUrl}
      target="_blank"
      UNSAFE_className={isConnectContact ? styles.alternateLink : undefined}
    >
      {displayText}
    </Link>
  ) : (
    <Text>{displayText}</Text>
  );
});

const convertToTimestamp = (calendarDate: CalendarDate, isEndDate = false) => {
  let date = new Date(calendarDate.toDate(getLocalTimeZone()));
  if (isEndDate) {
    date.setHours(23, 59, 59, 999);
  }
  return Math.floor(date.getTime() / 1000);
};

// Utility function to get color for callback status
const fetchColor = (status: CallbackStatus): string => {
  if (!status) return "";
  
  // Normalize status to handle inconsistencies
  const normalizedStatus = status === "IN_PROGRESS" ? "INPROGRESS" : status;
  return STATUS_COLORS[normalizedStatus] || "";
};

// Helper function to format call duration
const getFormattedCallLength = (startstamp: number, endstamp: number): string => {
  // Handle invalid or missing timestamps
  if (!startstamp || !endstamp || startstamp >= endstamp) return "N/A";
  
  // Calculate difference and format
  const differenceInSeconds = Math.max(0, endstamp - startstamp);
  const minutes = Math.floor(differenceInSeconds / 60);
  const seconds = differenceInSeconds % 60;
  
  // Optimize string creation with template literals
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

// Extract contact rendering logic outside of TableBody mapping
const renderContactRow = (
  contact: any, 
  contactIndex: number, 
  callbackContactId: string
) => {
  try {
    if (!contact || !contact.id) {
      return null;
    }
    
    const callLength = getFormattedCallLength(
      contact.initiationTimestamp || 0,
      contact.disconnectTimestamp || 0
    );
    
    return (
      <View
        key={`${contactIndex}-${callbackContactId}`}
        UNSAFE_style={{ marginBottom: "4px" }}
      >
        <TooltipTrigger delay={0}>
          <ContactLink
            value={contact.id}
            isConnectContact={true}
            index={contactIndex}
          />
          <Tooltip>{`Call duration: ${callLength}`}</Tooltip>
        </TooltipTrigger>
      </View>
    );
  } catch (error) {
    console.error("Error rendering contact row:", error);
    return null;
  }
};

const renderCaseId = (value: CallbackObjectType) => {
  const caseId = value?.callbackAttributes?.CASEID;
  const caseLink = value?.callbackAttributes?.C14E_CASE_LINK;
  if(caseLink) {
    return (
      <Link href={caseLink} target="_blank" UNSAFE_className={styles.alternateLink}>{caseId}</Link>
    );
  }
  return caseId || "-";
};

// Extract cell content rendering to reduce nesting
const renderCellContent = (value: CallbackObjectType, dataIndex: string) => {
  // Add defensive check for null/undefined value
  if (!value) return "-";
  
  try {
    if (dataIndex === "contactId") {
      return (
        <TooltipTrigger delay={0} containerPadding={0}>
          <ContactLink
            value={value.contactId || ""}
            isConnectContact={false}
          />
          <Tooltip>{value.contactId || ""}</Tooltip>
        </TooltipTrigger>
      );
    }
    
    if (dataIndex === "requestStatus") {
      return (
        <>
          <View
            UNSAFE_style={{
              height: 8,
              width: 8,
              borderRadius: 50,
              backgroundColor: fetchColor(
                value.requestStatus as CallbackStatus
              ),
              display: "inline-block",
              marginRight: 8,
            }}
          ></View>
          <Text>{value?.requestStatus || "-"}</Text>
        </>
      );
    }
    
    if (dataIndex === "customerName") {
      const firstName = value?.callbackAttributes?.FIRSTNAME || "";
      const lastName = value?.callbackAttributes?.LASTNAME || "";
      return (
        <Text>
          {`${firstName} ${lastName}`.trim() || "-"}
        </Text>
      );
    }
    
    if (dataIndex === "orgId") {
      return (
        <Text>
          {value?.callbackAttributes?.ORGID || value?.callbackAttributes?.ORG_ID || "-"}
        </Text>
      );
    }
    
    if (dataIndex === "connectContacts") {
      if (!value?.connectContacts?.length) return "-";
      
      return value.connectContacts.map(
        (contact, contactIndex) => renderContactRow(contact, contactIndex, value.contactId || "")
      );
    }
    
    if (dataIndex === "userLdap") {
      return <Text>{value?.contactHandleBy || "-"}</Text>;
    }
    
    if (dataIndex === "callbackTimeEpoch") {
      return (
        <Text>
          {value?.callbackTimeEpoch ? new Date(value.callbackTimeEpoch * 1000).toLocaleString() : "-"}
        </Text>
      );
    }

    if (dataIndex === "callbackSurface") {
      return <Text>{value?.callbackAttributes?.CALLBACK_SURFACE || "-"}</Text>;
    }

    if (dataIndex === "timezone") {
      return <Text>{value?.callbackAttributes?.TIMEZONE || "-"}</Text>;
    }

    if (dataIndex === "caseId") {
      return (
        <Text>
          {renderCaseId(value)}
        </Text>
      );
    }

    if (dataIndex === "acceptTimeEpoch") {
      return <Text>{value?.callbackAttributes?.CALLBACK_ANSWERED_TIME ? new Date(Number(value?.callbackAttributes?.CALLBACK_ANSWERED_TIME) * 1000).toLocaleString() : "-"}</Text>;
    }

    if (dataIndex === "dialTimeEpoch") {
      return <Text>{value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME ? new Date(Number(value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME) * 1000).toLocaleString() : "-"}</Text>;
    }
    
    // Default case with safe handling of dynamic properties
    try {
      const value1 = value?.[dataIndex as keyof CallbackObjectType];
      if (value1 === undefined || value1 === null) return "-";
      return typeof value1 === 'object' 
        ? JSON.stringify(value1) 
        : String(value1);
    } catch (err) {
      console.error(`Error rendering cell content for ${dataIndex}:`, err);
      return "-";
    }
  } catch (error) {
    console.error(`Error in renderCellContent for ${dataIndex}:`, error);
    return "-";
  }
};

const CallbackDashboard = () => {
  // Validate that callbackTableColumns is available
  const [validColumns, setValidColumns] = useState<typeof callbackTableColumns>(callbackTableColumns || []);
  
  useEffect(() => {
    try {
      // Ensure callbackTableColumns is available and valid
      if (callbackTableColumns && Array.isArray(callbackTableColumns) && callbackTableColumns.length > 0) {
        setValidColumns(callbackTableColumns);
      } else {
        console.error("callbackTableColumns is invalid:", callbackTableColumns);
        // Set at least one default column to prevent the cell count error
        setValidColumns([{
          title: "No columns available",
          dataIndex: "placeholder",
          key: "placeholder",
          width: "100%"
        }]);
      }
    } catch (error) {
      console.error("Error initializing columns:", error);
      // Set at least one default column to prevent the cell count error
      setValidColumns([{
        title: "Error loading columns",
        dataIndex: "placeholder",
        key: "placeholder",
        width: "100%"
      }]);
    }
  }, []);

  // Remove this state since CallbackDetails is commented out
  // const [showCallbackDetails, setShowCallbackDetails] = useState<boolean>(false);
  const [filteredCbs, setFilteredCbs] = useState<CallbackObjectType[]>([]);
  const [callbacks, setCallbacks] = useState<CallbackObjectType[]>([]);
  const [searchText, setSearchText] = useState<string>(() => {
    return localStorage.getItem("callbackSearchText") ?? "";
  });
  const [selectedCallbackStatus, setSelectedCallbackStatus] =
    useState<CallbackStatus | null>(() => {
      return (
        (localStorage.getItem("callbackStatus") as CallbackStatus) || "ALL"
      );
    });
  const [selectedCallbackType, setSelectedCallbackType] = useState<string>(() => {
    return localStorage.getItem("callbackType") || "ALL";
  });
  const [selectedCallbackSurface, setSelectedCallbackSurface] = useState<string>(() => {
    return localStorage.getItem("callbackSurface") || "ALL";
  });
  const [isRotating, setIsRotating] = useState(false);
  const initialStartDate = today(getLocalTimeZone()).subtract({ days: 1 });
  const initialEndDate = today(getLocalTimeZone());
  const storedStart = localStorage.getItem("callbackStartDate");
  const storedEnd = localStorage.getItem("callbackEndDate");
  const defaultStartDate = storedStart
    ? parseDate(storedStart)
    : initialStartDate;
  const defaultEndDate = storedEnd ? parseDate(storedEnd) : initialEndDate;

  const [dateRange, setDateRange] = useState({
    start: defaultStartDate,
    end: defaultEndDate,
  });
  const [timestamps, setTimestamps] = useState({
    startTime: convertToTimestamp(defaultStartDate),
    endTime: convertToTimestamp(defaultEndDate, true),
  });

  useEffect(() => {
    getScheduledCallbackList();
  }, []);

  const handleDateChange = (
    range: { start: CalendarDate; end: CalendarDate } | null
  ) => {
    if (range) {
      setDateRange(range);
      const newStartTime = convertToTimestamp(range.start);
      const newEndTime = convertToTimestamp(range.end, true);
      
      setTimestamps({
        startTime: newStartTime,
        endTime: newEndTime,
      });
      
      localStorage.setItem("callbackStartDate", range.start.toString());
      localStorage.setItem("callbackEndDate", range.end.toString());
      
      getScheduledCallbackListApi(newStartTime, newEndTime)
        .then((res: CallbackListAPIResponse) => {
          // @ts-ignore
          const result = res.length > 0 ? res?.sort((a, b) => b.callbackTimeEpoch - a.callbackTimeEpoch) : [];
          setCallbacks(result);
          applyFilters(result, searchText, selectedCallbackStatus, selectedCallbackType, selectedCallbackSurface);
        })
        .catch((error) => {
          console.error("Failed to fetch scheduled callback details:", error);
        });
    }
  };
  
  const getScheduledCallbackList = async () => {
    setIsRotating(true);
    try {
      const res: CallbackListAPIResponse = await getScheduledCallbackListApi(
        timestamps.startTime,
        timestamps.endTime
      );
      // @ts-ignore
      const result = res.length > 0 ? res?.sort((a, b) => b.callbackTimeEpoch - a.callbackTimeEpoch) : [];
      setCallbacks(result);
      applyFilters(result, searchText, selectedCallbackStatus, selectedCallbackType, selectedCallbackSurface);
    } catch (error) {
      console.error("Failed to fetch scheduled callback details:", error);
    } finally {
      setIsRotating(false);
    }
  };

  const applyFilters = (data: CallbackObjectType[], searchValue: string, status: CallbackStatus | null, type: string = "ALL", surface: string = "ALL") => {
    if (!data || !Array.isArray(data)) {
      setFilteredCbs([]);
      return;
    }
    
    // Always start with the original data
    const originalData = data;
    
    // Apply all filters at once to ensure they're combined properly
    const filtered = originalData.filter(item => {
      let matchesStatus = true;
      let matchesSearch = true;
      let matchesType = true;
      let matchesSurface = true;
      
      // Status filter
      if (status && status !== "ALL") {
        const normalizedStatus = status === "IN_PROGRESS" ? "INPROGRESS" : status;
        const itemStatus = item.requestStatus === "IN_PROGRESS" ? "INPROGRESS" : item.requestStatus;
        matchesStatus = itemStatus === normalizedStatus;
      }
      
      // Type filter
      if (type && type !== "ALL") {
        matchesType = item.callbackType === type;
      }

      // Surface filter
      if (surface && surface !== "ALL") {
        matchesSurface = item.callbackAttributes?.CALLBACK_SURFACE === surface;
      }
      
      // Search filter
      const searchLower = searchValue?.trim().toLowerCase();
      if (searchLower) {
        // Safely access potentially undefined properties
        const firstName = item.callbackAttributes?.FIRSTNAME || "";
        const lastName = item.callbackAttributes?.LASTNAME || "";
        const userLdap = item.contactHandleBy || "";
        const orgId = item.callbackAttributes?.ORG_ID || item.callbackAttributes?.ORGID || "";
        
        // Create lowercase versions of strings once
        const contactIdLower = (item.contactId || "").toLowerCase();
        const phoneNumberLower = (item.phoneNumber || "").toLowerCase();
        const productFamilyLower = (item.productFamily || "").toLowerCase();
        const callbackTypeLower = (item.callbackType || "").toLowerCase();
        const requestStatusLower = (item.requestStatus || "").toLowerCase();
        const firstNameLower = firstName.toLowerCase();
        const lastNameLower = lastName.toLowerCase();
        const fullNameLower = `${firstNameLower} ${lastNameLower}`.trim();
        const userLdapLower = userLdap.toLowerCase();
        const orgIdLower = orgId.toLowerCase();
        const caseIdLower = (item.callbackAttributes?.CASEID || "").toLowerCase();
        const callbackSurfaceLower = (item.callbackAttributes?.CALLBACK_SURFACE || "").toLowerCase();
        
        // Safely create connectContacts string
        let allConnectContacts = "";
        if (item.connectContacts && Array.isArray(item.connectContacts)) {
          allConnectContacts = item.connectContacts
            .map(contact => contact.id || "")
            .filter(Boolean)
            .join(",");
        }

        // Check if any field contains the search term
        matchesSearch = (
          contactIdLower.includes(searchLower) ||
          phoneNumberLower.includes(searchLower) ||
          productFamilyLower.includes(searchLower) ||
          firstNameLower.includes(searchLower) ||
          lastNameLower.includes(searchLower) ||
          fullNameLower.includes(searchLower) ||
          callbackTypeLower.includes(searchLower) ||
          requestStatusLower.includes(searchLower) ||
          userLdapLower.includes(searchLower) ||
          allConnectContacts.includes(searchLower) ||
          orgIdLower.includes(searchLower) ||
          caseIdLower.includes(searchLower) ||
          callbackSurfaceLower.includes(searchLower)
        );
      }
      
      // An item must match all filters to be included
      return matchesStatus && matchesSearch && matchesType && matchesSurface;
    });
    
    setFilteredCbs(filtered);
  };

  const handleSearch = (value: any) => {
    setSearchText(value);
    localStorage.setItem("callbackSearchText", value);
    applyFilters(callbacks, value, selectedCallbackStatus, selectedCallbackType, selectedCallbackSurface);
  };

  const handleStatusChange = (value: any) => {
    setSelectedCallbackStatus(value);
    localStorage.setItem("callbackStatus", value);
    applyFilters(callbacks, searchText, value, selectedCallbackType, selectedCallbackSurface);
  };

  const handleTypeChange = (value: any) => {
    setSelectedCallbackType(value);
    localStorage.setItem("callbackType", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, value, selectedCallbackSurface);
  };

  const handleSurfaceChange = (value: any) => {
    setSelectedCallbackSurface(value);
    localStorage.setItem("callbackSurface", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, selectedCallbackType, value);
  };

  return (
    <View UNSAFE_className={styles.container}>
      <>
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          UNSAFE_className={styles.header}    
        >
          <Flex direction={"row"} alignItems={"center"} gap={"size-250"}>
            <Timer
              getScheduledCallbackList={getScheduledCallbackList}
              isRotating={isRotating}
            />
          </Flex>
          <Flex direction={"row"} alignItems={"center"} gap={"size-250"}  UNSAFE_style={{ padding: "0px 24px 0px 0px" }}>
            <SearchField
              width="240px"
              placeholder="Filter by keyword"
              value={searchText}
              onChange={handleSearch}
            />
          </Flex>
        </Flex>

        <View UNSAFE_className={styles.content}>
          <Flex
            direction={"row"}
            UNSAFE_style={{ padding: "0px 24px 0px 0px" }}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Flex>
              <Heading level={5}>
                Total callbacks: {filteredCbs.length}
              </Heading>
            </Flex>

            <Flex gap="size-250" alignItems="center">
              <DateRangePicker
                label="Date Range"
                labelAlign="start"
                labelPosition="side"
                width="315px"
                value={dateRange}
                onChange={handleDateChange}
                maxValue={today(getLocalTimeZone())}
              />
              <Flex direction="row" gap="size-150">
                <Picker
                  label="Callback Surface"
                  labelAlign="start"
                  labelPosition="side"
                  items={callbackSurface}
                  selectedKey={selectedCallbackSurface}
                  onSelectionChange={handleSurfaceChange}
                >
                  {(surface) => <Item key={surface.id}>{surface.name}</Item>}
                </Picker>
                <Picker
                  label="Callback status"
                  labelAlign="start"
                  labelPosition="side"
                  items={callbackStatus}
                  selectedKey={selectedCallbackStatus}
                  onSelectionChange={handleStatusChange}
                >
                  {(status) => <Item key={status.id}>{status.name}</Item>}
                </Picker>
                <Picker
                  label="Type"
                  labelAlign="start"
                  labelPosition="side"
                  items={callbackTypes}
                  selectedKey={selectedCallbackType}
                  onSelectionChange={handleTypeChange}
                >
                  {(type) => <Item key={type.id}>{type.name}</Item>}
                </Picker>
              </Flex>
            </Flex>
          </Flex>
          
          {/* Only render the table if we have valid columns */}
          {validColumns.length > 0 ? (
            <TableView
              aria-label="scheduled-callbacks"
              height="68vh"
              UNSAFE_style={{
                padding: "0px 24px 0px 0px",
                scrollbarWidth: "thin",
              }}
              overflowMode="wrap"
              selectionMode="single"
              selectionStyle="highlight"
              renderEmptyState={() => (
                <View UNSAFE_className={styles.emptyState}>
                  <Text>No data available</Text>
                </View>
              )}
            >
              <TableHeader>
                {validColumns.map((column) => {return column.dataIndex === 'contactId' ? (
                  <Column 
                    key={column.dataIndex || Math.random().toString()} 
                    //minWidth={column.width as ColumnStaticSize} 
                    allowsResizing
                    // maxWidth={column.width as ColumnStaticSize}
                    // width={column.width as ColumnStaticSize}
                    maxWidth={500}
                    // wrap={true}
                  >
                    {column.title || ""}
                  </Column>
                ):
                <Column 
                  key={column.dataIndex || Math.random().toString()} 
                  minWidth={column.width as ColumnStaticSize} 
                  allowsResizing
                >
                  {column.title || ""}
                </Column>
                })}
              </TableHeader>

              <TableBody>
                {
                  filteredCbs && filteredCbs.length > 0 
                    ? filteredCbs.map((value: CallbackObjectType, index) => (
                        <Row key={value?.contactId || `row-${index}`}>
                          {validColumns.map((column) => (
                            <Cell key={`${value?.contactId || index}-${column.dataIndex || index}`} >
                              {renderCellContent(value, column.dataIndex || "")}
                            </Cell>
                          ))}
                        </Row>
                      ))
                    : []
                }
              </TableBody>
            </TableView>
          ) : (
            <View UNSAFE_className={styles.emptyState}>
              <Text>Loading table columns...</Text>
            </View>
          )}
        </View>
      </>
    </View>
  );
};

export default React.memo(CallbackDashboard);
